import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  Search,
  Building,
  Package,
  Gauge,
  Eye,
  Loader2,
  Hash,
  MapPin,
  X,
  Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category options
const CATEGORIES = [
  { value: 'all', label: 'All Categories', icon: Search },
  { value: 'buildings', label: 'Buildings', icon: Building },
  { value: 'assets', label: 'Assets', icon: Package },
  { value: 'instrumentation', label: 'Instrumentation', icon: Gauge },
];

export function GlobalSearchPanel({ 
  isOpen,
  onClose,
  onNavigate
}) {
  const { token } = useAuth();
  const { currentTour } = useTour();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({
    buildings: [],
    assets: [],
    instrumentation: []
  });

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    setLoading(true);
    try {
      const [buildingsRes, assetsRes, instrumentationRes] = await Promise.all([
        fetch(`${API_URL}/api/buildings?tour_id=${currentTour.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/equipment/tour/${currentTour.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/instrumentation?tour_id=${currentTour.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const [buildings, assets, instrumentation] = await Promise.all([
        buildingsRes.ok ? buildingsRes.json() : [],
        assetsRes.ok ? assetsRes.json() : [],
        instrumentationRes.ok ? instrumentationRes.json() : []
      ]);

      setResults({ buildings, assets, instrumentation });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token]);

  useEffect(() => {
    if (isOpen) {
      fetchAllData();
    }
  }, [isOpen, fetchAllData]);

  // Filter results based on search term and category
  const filterItems = (items, type) => {
    if (!searchTerm) return items;
    
    const term = searchTerm.toLowerCase();
    return items.filter(item => {
      switch (type) {
        case 'buildings':
          return item.name?.toLowerCase().includes(term) ||
                 item.building_number?.toLowerCase().includes(term) ||
                 item.building_type?.toLowerCase().includes(term);
        case 'assets':
          return item.name?.toLowerCase().includes(term) ||
                 item.tag_number?.toLowerCase().includes(term) ||
                 item.equipment_type?.toLowerCase().includes(term);
        case 'instrumentation':
          return item.tag_number?.toLowerCase().includes(term) ||
                 item.name?.toLowerCase().includes(term) ||
                 item.instrument_type?.toLowerCase().includes(term);
        default:
          return true;
      }
    });
  };

  const filteredBuildings = categoryFilter === 'all' || categoryFilter === 'buildings' 
    ? filterItems(results.buildings, 'buildings') : [];
  const filteredAssets = categoryFilter === 'all' || categoryFilter === 'assets' 
    ? filterItems(results.assets, 'assets') : [];
  const filteredInstrumentation = categoryFilter === 'all' || categoryFilter === 'instrumentation' 
    ? filterItems(results.instrumentation, 'instrumentation') : [];

  const totalResults = filteredBuildings.length + filteredAssets.length + filteredInstrumentation.length;

  // Handle navigation
  const handleNavigate = async (item) => {
    console.log('GlobalSearchPanel: handleNavigate called', { item, onNavigate: typeof onNavigate });
    
    if (!item.sweep_id) {
      console.warn('GlobalSearchPanel: No sweep_id for item', item.name);
      return;
    }
    
    if (typeof onNavigate !== 'function') {
      console.error('GlobalSearchPanel: onNavigate is not a function', onNavigate);
      alert('Navigation not available. Please wait for the 3D viewer to load.');
      return;
    }
    
    try {
      console.log('GlobalSearchPanel: Calling onNavigate with', item.sweep_id, item.rotation);
      await onNavigate(item.sweep_id, {
        rotation: item.rotation,
        transitionTime: 1500
      });
      onClose();
    } catch (error) {
      console.error('GlobalSearchPanel: Navigation error', error);
    }
  };

  // Render result item
  const renderResultItem = (item, type) => {
    const getIcon = () => {
      switch (type) {
        case 'buildings': return <Building className="h-4 w-4 text-blue-500" />;
        case 'assets': return <Package className="h-4 w-4 text-green-500" />;
        case 'instrumentation': return <Gauge className="h-4 w-4 text-orange-500" />;
        default: return null;
      }
    };

    const getTitle = () => {
      switch (type) {
        case 'buildings': return item.name;
        case 'assets': return item.name;
        case 'instrumentation': return item.tag_number;
        default: return '';
      }
    };

    const getSubtitle = () => {
      switch (type) {
        case 'buildings': return item.building_number ? `#${item.building_number}` : item.building_type;
        case 'assets': return item.tag_number || item.equipment_type;
        case 'instrumentation': return item.name || item.instrument_type;
        default: return '';
      }
    };

    const getType = () => {
      switch (type) {
        case 'buildings': return item.building_type;
        case 'assets': return item.equipment_type;
        case 'instrumentation': return item.instrument_type;
        default: return '';
      }
    };

    return (
      <div 
        key={`${type}-${item.id}`}
        className="p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="mt-0.5">
              {getIcon()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-medium truncate">{getTitle()}</p>
                <Badge variant="secondary" className="text-[10px] capitalize">
                  {type.replace('instrumentation', 'instrument')}
                </Badge>
              </div>
              {getSubtitle() && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Hash className="h-3 w-3" />
                  {getSubtitle()}
                </p>
              )}
              {getType() && (
                <p className="text-xs text-muted-foreground mt-1 capitalize">
                  {getType()?.replace(/_/g, ' ')}
                </p>
              )}
              {item.sweep_id && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  Position saved
                </p>
              )}
            </div>
          </div>
          {item.sweep_id && (
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => handleNavigate(item)}
              title="Go to location"
            >
              <Eye className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-[400px] sm:w-[540px] p-0">
        <SheetHeader className="p-6 pb-4 border-b">
          <div className="flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            <SheetTitle>Global Search</SheetTitle>
          </div>
          <SheetDescription>
            Search across buildings, assets, and instrumentation
          </SheetDescription>
        </SheetHeader>

        <div className="p-4 border-b space-y-3">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, tag, or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              autoFocus
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => setSearchTerm('')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Category Filter */}
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger>
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>
                  <div className="flex items-center gap-2">
                    <cat.icon className="h-4 w-4" />
                    {cat.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Results Count */}
          {!loading && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{totalResults} result{totalResults !== 1 ? 's' : ''} found</span>
              {searchTerm && (
                <span>for "{searchTerm}"</span>
              )}
            </div>
          )}
        </div>

        {/* Results */}
        <ScrollArea className="h-[calc(100vh-280px)]">
          <div className="p-4 space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : totalResults === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Search className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No results found</p>
                {searchTerm && (
                  <p className="text-sm mt-1">Try a different search term</p>
                )}
              </div>
            ) : (
              <>
                {/* Buildings Section */}
                {filteredBuildings.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Building className="h-4 w-4" />
                      Buildings ({filteredBuildings.length})
                    </div>
                    {filteredBuildings.map(item => renderResultItem(item, 'buildings'))}
                  </div>
                )}

                {/* Assets Section */}
                {filteredAssets.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Package className="h-4 w-4" />
                      Assets ({filteredAssets.length})
                    </div>
                    {filteredAssets.map(item => renderResultItem(item, 'assets'))}
                  </div>
                )}

                {/* Instrumentation Section */}
                {filteredInstrumentation.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Gauge className="h-4 w-4" />
                      Instrumentation ({filteredInstrumentation.length})
                    </div>
                    {filteredInstrumentation.map(item => renderResultItem(item, 'instrumentation'))}
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

export default GlobalSearchPanel;
